<template>
  <v-dialog :value="value" @input="onDialogInput" max-width="800px" @click:outside="onCancel" scrollable persistent>
    <v-card>
      <v-toolbar flat color="primary" class="white--text">
        <v-toolbar-title class="font-weight-bold">{{ $t('memberDataSection.temporaryPassword') }}</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="px-4 py-4" style="max-height: 480px">
        <h2 class="text-h6 black--text mb-3">{{ $t('memberDataSection.generateNewTemporaryPassword') }}</h2>
        <p class="text-body-2">{{ $t('password.assignPasswordMessage') }}</p>
        <div class="d-flex align-center mt-1" style="gap: 12px">
          <v-btn plain color="primary" class="font-weight-bold" style="padding: 0" @click="generatePassword">
            {{ $t('password.generateSecurePassword') }}
          </v-btn>
          <v-text-field v-if="generatedPassword.length > 0" :value="generatedPassword" readonly outlined dense
            hide-details="auto" style="max-width: 140px" @click="$event.target.select()" @focus="$event.target.select()"></v-text-field>
          <v-btn plain color="primary" class="font-weight-bold" style="padding: 0"
            @click="copyToClipboard" v-if="generatedPassword.length > 0">
            {{ $t('copy') }}<v-icon right>mdi-content-copy</v-icon>
          </v-btn>
        </div>

        <v-form @submit.prevent="onFormSubmit" ref="form" v-model="form.valid" lazy-validation class="mt-6" style="gap: 12px">
          <v-row>
            <v-col cols="6">
              <label class="d-flex flex-column">
                <span class="mb-1">{{ $t('password.newPassword') }}</span>
                <v-text-field v-model="form.data.password" :rules="form.rules.password"
                  :type="showPassword ? 'text' : 'password'" outlined hide-details="auto" required dense
                  :placeholder="$t('password.enterNewPassword')" validate-on-blur
                  :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" @click:append="showPassword = !showPassword"></v-text-field>
              </label>
            </v-col>
            <v-col cols="6">
              <label class="d-flex flex-column">
                <span class="mb-1">{{ $t('password.confirmNewPassword') }}</span>
                <v-text-field v-model="form.data.confirmPassword" :rules="form.rules.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'" outlined hide-details="auto" required dense
                  :placeholder="$t('password.reenterNewPassword')" validate-on-blur
                  :append-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'" @click:append="showConfirmPassword = !showConfirmPassword"></v-text-field>
              </label>
            </v-col>
          </v-row>
        </v-form>

        <div v-if="historyEntries.length > 0">
          <v-divider class="mt-6 mb-6"></v-divider>
          <h2 class="text-h6 mb-3 black--text">{{ $t('memberDataSection.temporaryPasswordHistory') }}</h2>
          <ul style="list-style: none" class="pl-0 d-flex flex-column">
            <li v-for="entry in historyEntries" :key="entry.id" class="mb-3 text-body-2">
              <span v-if="entry.updatedDate">
                {{ $t('memberDataSection.tempPasswordSentUpdated', { date: entry.sentDate, updatedDate: entry.updatedDate }) }}
              </span>
              <template v-else>
                {{ $t('memberDataSection.tempPasswordSentOn', { date: entry.sentDate }) }}
                <a class="ml-3 font-weight-medium" style="color: #a75300" @click.prevent="$emit('resend', userId)">{{ $t('memberDataSection.resend') }}</a>
                <span class="mx-1 grey--text">|</span>
                <a class="font-weight-medium" style="color: #a75300" @click.prevent="$emit('reset', userId)">{{ $t('memberDataSection.resetPassword') }}</a>
              </template>
            </li>
          </ul>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="justify-end">
        <v-btn outlined color="primary" @click="onCancel">{{ $t('cancel') }}</v-btn>
        <v-btn color="primary" @click="onFormSubmit">{{ $t('confirm') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import LoadingMixin from '@/mixins/LoadingMixin';
import UserService from '@/_services/UserService';
import moment from 'moment';

export default {
  name: 'TemporaryPasswordDialog',
  mixins: [LoadingMixin],
  props: {
    value: { type: Boolean, default: false },
    userId: { type: [String, Number], required: false },
  },
  watch: {
    value: async function (isOpen) {
      if (!isOpen) {
        this.onCancel(false);
      } else {
        this.showPassword = false;
        this.showConfirmPassword = false;
        await this.fetchPasswordHistories();
      }
    },
  },
  computed: {
    // Normalise raw histories into the design's "sent on / updated by User on" rows.
    // Field names are defensive — exact temp-password history shape comes from the backend.
    historyEntries() {
      return (this.passwordHistories || []).map((h, i) => {
        const updated = h.passwordUpdatedAt || h.updatedByUserAt || (h.type === 'UPDATE' ? h.updatedAt : null);
        return {
          id: h.id != null ? h.id : i,
          sentDate: this.formatDate(h.createdAt || h.sentAt),
          updatedDate: updated ? this.formatDate(updated) : null,
        };
      });
    },
  },
  data() {
    return {
      showPassword: false,
      showConfirmPassword: false,
      passwordHistories: [],
      generatedPassword: '',
      form: {
        valid: true,
        data: { password: '', confirmPassword: '' },
        rules: {
          password: [
            (v) => !!v || this.$t('addEdituser.pswRequired'),
            (v) => (!v ? true : v.length >= 8 || this.$t('password.min8CharacterMessage')),
          ],
          confirmPassword: [
            (v) => !!v || this.$t('addEdituser.pswRequired'),
            (v) => v === this.form.data.password || this.$t('passwordDoesNotMatch'),
          ],
        },
      },
    };
  },
  methods: {
    generatePassword() {
      const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@_+-=';
      let password = '';
      for (let i = 0; i < 8; i++) {
        password += charset[Math.floor(Math.random() * charset.length)];
      }
      this.generatedPassword = password;
      this.form.data.password = password;
      this.form.data.confirmPassword = password;
    },
    copyToClipboard() {
      navigator.clipboard.writeText(this.generatedPassword);
    },
    async fetchPasswordHistories() {
      if (!this.userId) return;
      try {
        const response = await UserService.getAllPasswordHistoriesByUserId(this.userId);
        this.passwordHistories = response.data || [];
      } catch (error) {
        this.passwordHistories = [];
      }
    },
    formatDate(dateString) {
      return dateString ? moment(dateString).format('DD-MM-YYYY') : '';
    },
    onDialogInput(visible) {
      if (!visible) this.onCancel(false);
      this.$emit('input', visible);
    },
    onFormSubmit() {
      if (!this.$refs.form || !this.$refs.form.validate()) return;
      this.$emit('passwordSet', { password: this.form.data.password, userId: this.userId });
    },
    onCancel(shouldEmit = true) {
      this.passwordHistories = [];
      this.generatedPassword = '';
      this.showPassword = false;
      this.showConfirmPassword = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
        this.$refs.form.reset();
      }
      if (shouldEmit) {
        this.$emit('cancel');
        this.$emit('input', false);
      }
    },
  },
};
</script>
